import { LitElement, html } from 'lit';
import styles from './studio-pagination.css.mjs';
import '../studio-icon/studio-icon.mjs';

export class StudioPagination extends LitElement {
  static styles = styles;

  static properties = {
    totalPages: { type: Number, reflect: true },
    currentPage: { type: Number, reflect: true },
    maxVisible: { type: Number, reflect: true },
  };

  static validSizes = ['large', 'medium', 'small'];

  constructor() {
    super();
    this.totalPages = 5;
    this.currentPage = 1;
    this.maxVisible = 5;
    this._eventListeners = [];
  }

  set totalPages(value) {
    const num = Number(value);
    this._totalPages = num >= 1 ? Math.floor(num) : 5;
    this._clampCurrentPage();
  }

  get totalPages() {
    return this._totalPages;
  }

  set currentPage(value) {
    const num = Number(value);
    this._currentPage = num >= 1 ? Math.floor(num) : 1;
    this._clampCurrentPage();
  }

  get currentPage() {
    return this._currentPage;
  }

  set maxVisible(value) {
    const num = Number(value);
    this._maxVisible = num >= 3 ? Math.floor(num) : 5;
  }

  get maxVisible() {
    return this._maxVisible;
  }

  _clampCurrentPage() {
    if (this._currentPage > this._totalPages) {
      this._currentPage = this._totalPages;
    }
    if (this._currentPage < 1) {
      this._currentPage = 1;
    }
  }

  firstUpdated() {
    const buttons = this.renderRoot.querySelectorAll('.page-item');
    buttons.forEach(button => {
      if (button.hasAttribute('disabled') || button.classList.contains('ellipsis')) return;

      const setState = (state, add = true) => {
        if (!button.disabled && !button.classList.contains('selected')) {
          button.classList[add ? 'add' : 'remove'](state);
        }
      };

      const listeners = [
        { event: 'focus', handler: () => setState('focus') },
        { event: 'blur', handler: () => setState('focus', false) },
        { event: 'mouseenter', handler: () => setState('hover') },
        { event: 'mouseleave', handler: () => {
          setState('hover', false);
          setState('press', false);
        }},
        { event: 'mousedown', handler: () => setState('press') },
        { event: 'mouseup', handler: () => {
          setState('press', false);
          setState('focus');
        }},
        { event: 'keydown', handler: (e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !button.disabled) {
            e.preventDefault();
            button.click();
          }
        }},
      ];

      listeners.forEach(({ event, handler }) => {
        button.addEventListener(event, handler);
        this._eventListeners.push({ element: button, event, handler });
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this._eventListeners = [];
  }

  render() {
    const pages = this._generatePageItems();
    return html`
      <nav class="pagination" role="navigation" aria-label="Pagination">
        <button
          class="page-item prev ${this.currentPage === 1 ? 'disabled' : ''}"
          @click="${this._handlePrevClick}"
          ?disabled="${this.currentPage === 1}"
          aria-label="Previous page"
        >
          <slot name="prev-icon">
            <studio-icon name="chevron-left"></studio-icon>
          </slot>
        </button>
        ${pages.map(
          (page, index) => html`
            <button
              class="page-item ${page === this.currentPage ? 'selected' : ''} ${page === '...' ? 'ellipsis' : ''}"
              ?disabled="${page === '...'}"
              @click="${() => this._handlePageClick(page)}"
              aria-label="${page === '...' ? 'More pages' : `Page ${page}`}"
              aria-current="${page === this.currentPage ? 'page' : null}"
            >
              <slot name="page-${index}">${page}</slot>
            </button>
          `
        )}
        <button
          class="page-item next ${this.currentPage === this.totalPages ? 'disabled' : ''}"
          @click="${this._handleNextClick}"
          ?disabled="${this.currentPage === this.totalPages}"
          aria-label="Next page"
        >
          <slot name="next-icon">
            <studio-icon name="chevron-right"></studio-icon>
          </slot>
        </button>
        <slot></slot>
      </nav>
    `;
  }

  _generatePageItems() {
    const pages = [];
    const max = Math.min(this.maxVisible, this.totalPages);
    const half = Math.floor(max / 2);
    let start = Math.max(1, this.currentPage - half);
    let end = Math.min(this.totalPages, start + max - 1);

    if (end === this.totalPages) {
      start = Math.max(1, end - max + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < this.totalPages) {
      if (end < this.totalPages - 1) pages.push('...');
      pages.push(this.totalPages);
    }

    return pages;
  }

  _handlePrevClick() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this._dispatchPageChange();
    }
  }

  _handleNextClick() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this._dispatchPageChange();
    }
  }

  _handlePageClick(page) {
    if (page !== '...' && page !== this.currentPage) {
      this.currentPage = page;
      this._dispatchPageChange();
    }
  }

  _dispatchPageChange() {
    this.dispatchEvent(
      new CustomEvent('page-change', {
        detail: { page: this.currentPage },
        bubbles: true,
        composed: true,
      })
    );
  }
}

customElements.define('studio-pagination', StudioPagination);